import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faPlus,
  faPen,
  faImage,
  faBan,
  faSearch,
  faStore,
  faCircleExclamation,
  faCircleCheck,
  faXmark,
  faLink,
  faCopy,
  faVolumeHigh,
  faVolumeXmark,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

import { useHerKart } from "./hooks/useHerKart";
import WebpImgUploader from "../purpledoor/modules/components/WebpImgUploader";
import AILightComponent from "../../components/AILightComponent";

const emptyForm = {
  storeCode: "",
  storeSuffix: "",
  storeName: "",
  storeHeadline: "",
  isBrandOnly: false,
  isInnovationOnly: false,
  isSampleOnly: false,
  isActive: true,
  storeImage: "",
  tts: "",
  locality: "",
  city: "",
};

const HerKartManageStores = () => {
  const authState = useSelector((state) => state.authReducer);
  const { fetchMyStores, createStore, updateStore, deactivateStore } = useHerKart();

  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [uploaderForId, setUploaderForId] = useState(null); // store _id currently changing image, or "new" for the form
  const [confirmDeactivateId, setConfirmDeactivateId] = useState(null);

  const [enableGenAIHelp, setEnableGenAIHelp] = useState(false);
  const toggleGenAIHelp = () => setEnableGenAIHelp((prev) => !prev);

  const [isSpeakingTTS, setIsSpeakingTTS] = useState(false);
  const handleToggleSpeakTTS = () => {
    if (!("speechSynthesis" in window)) {
      setMessage({ type: "error", text: "Voice playback isn't supported in this browser." });
      return;
    }

    if (isSpeakingTTS) {
      window.speechSynthesis.cancel();
      setIsSpeakingTTS(false);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(form.tts);
    utterance.lang = "en-IN";
    utterance.onend = () => setIsSpeakingTTS(false);
    utterance.onerror = () => setIsSpeakingTTS(false);

    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
    setIsSpeakingTTS(true);
  };

  const [linkForId, setLinkForId] = useState(null);

  const buildStoreLink = (store) =>
    `https://www.think5whys.com/herkart/products/${store.storeCode}`;

  const handleCopyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      setMessage({ type: "success", text: "Link copied to clipboard." });
    } catch (e) {
      console.error("Copy link error:", e);
      setMessage({ type: "error", text: "Could not copy link." });
    }
  };

  const handleShareOnWhatsApp = (store) => {
    const link = buildStoreLink(store);
    const text = encodeURIComponent(`Check out ${store.storeName} on HerKart: ${link}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  useEffect(() => {
    return () => {
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    };
  }, []);

  const load = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const response = await fetchMyStores();
      if (response?.success) {
        setStores(response.stores || []);
      }
    } catch (e) {
      console.error("Stores fetch error:", e);
      setMessage({ type: "error", text: "Could not load your stores." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredStores = useMemo(() => {
    let list = [...stores];

    if (statusFilter !== "All") {
      list = list.filter((s) => (statusFilter === "Active" ? s.isActive : !s.isActive));
    }

    if (search.trim()) {
      const keyword = search.toLowerCase();
      list = list.filter((s) =>
        [s.storeName, s.storeCode, s.storeHeadline].join(" ").toLowerCase().includes(keyword)
      );
    }

    return list;
  }, [stores, search, statusFilter]);

  const openAddForm = () => {
    setEditingId(null);
    setForm({ ...emptyForm, storeCode: authState?.user?.think5whyscode || "", storeSuffix: "", });
    setShowForm(true);
    setMessage(null);
  };

  const openEditForm = (store) => {
    setEditingId(store._id);
    const parts = (store.storeCode || "").split("_");
    setForm({
      storeCode: parts[0] || "",
      storeSuffix: parts[1] || "",
      storeName: store.storeName || "",
      storeHeadline: store.storeHeadline || "",
      isBrandOnly: !!store.isBrandOnly,
      isInnovationOnly: !!store.isInnovationOnly,
      isSampleOnly: !!store.isSampleOnly,
      isActive: !!store.isActive,
      storeImage: store.storeImage || "",
      tts: store.tts || "",
      locality: store.locality || "",
      city: store.city || "",
    });
    setShowForm(true);
    setMessage(null);
  };

  const closeForm = () => {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    setIsSpeakingTTS(false);
    setEnableGenAIHelp(false);
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "storeSuffix") {
      const cleaned = value.replace(/[^a-zA-Z]/g, "").slice(0, 5);

      setForm((prev) => ({
        ...prev,
        storeSuffix: cleaned.toLowerCase(),
      }));
      return;
    }
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!form.storeCode.trim() || !form.storeName.trim()) {
      setMessage({ type: "error", text: "Store Code and Store Name are required." });
      return;
    }

    setSaving(true);
    const payload = {
      ...form,
      storeCode: form.storeSuffix
        ? `${form.storeCode}_${form.storeSuffix}`
        : form.storeCode,
    };
    try {
      if (editingId) {
        const response = await updateStore(editingId, payload);
        if (response?.success) {
          setStores((prev) => prev.map((s) => (s._id === editingId ? response.store : s)));
          setMessage({ type: "success", text: "Store updated successfully." });
          closeForm();
        } else {
          setMessage({ type: "error", text: "Failed to update store." });
        }
      } else {
        const response = await createStore(payload);
        if (response?.success) {
          setStores((prev) => [response.store, ...prev]);
          setMessage({ type: "success", text: "Store created successfully." });
          closeForm();
        } else {
          setMessage({ type: "error", text: response?.message || "Failed to create store." });
        }
      }
    } catch (e) {
      console.error("Store save error:", e);
      setMessage({ type: "error", text: "Failed to save store." });
    } finally {
      setSaving(false);
    }
  };

  const confirmDeactivate = async (id) => {
    try {
      const response = await deactivateStore(id);
      if (response?.success) {
        setStores((prev) => prev.map((s) => (s._id === id ? response.store : s)));
        setMessage({ type: "success", text: "Store deactivated." });
      } else {
        setMessage({ type: "error", text: "Failed to deactivate store." });
      }
    } catch (e) {
      console.error("Deactivate error:", e);
      setMessage({ type: "error", text: "Failed to deactivate store." });
    } finally {
      setConfirmDeactivateId(null);
    }
  };

  const handleReactivate = async (id) => {
    try {
      const response = await updateStore(id, { isActive: true });
      if (response?.success) {
        setStores((prev) => prev.map((s) => (s._id === id ? response.store : s)));
        setMessage({ type: "success", text: "Store reactivated." });
      } else {
        setMessage({ type: "error", text: "Failed to reactivate store." });
      }
    } catch (e) {
      console.error("Reactivate error:", e);
      setMessage({ type: "error", text: "Failed to reactivate store." });
    }
  };

  const handleImageChange = async (storeId, filename, url) => {
    if (storeId === "new") {
      setForm((prev) => ({ ...prev, storeImage: url }));
      setUploaderForId(null);
      return;
    }

    try {
      const response = await updateStore(storeId, { storeImage: url });
      if (response?.success) {
        setStores((prev) => prev.map((s) => (s._id === storeId ? response.store : s)));
        setMessage({ type: "success", text: "Store image updated." });
      }
    } catch (e) {
      console.error("Image update error:", e);
      setMessage({ type: "error", text: "Failed to update store image." });
    } finally {
      setUploaderForId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-[#050816]/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <Link
              to="/herkart/dashboard"
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </Link>
            <div>
              <h1 className="text-xl font-bold">My Private Stores</h1>
              <p className="text-xs text-slate-200">
                Manage your FoundHer storefronts
              </p>
            </div>
          </div>

          <button
            onClick={openAddForm}
            title="Add New Store"
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-pink-600 hover:bg-pink-500"
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-6">
        {message && (
          <div
            className={`mb-4 flex items-center gap-3 rounded-2xl border p-4 text-sm ${
              message.type === "success"
                ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-200"
                : "border-red-500/20 bg-red-500/10 text-red-200"
            }`}
          >
            <FontAwesomeIcon
              icon={
                message.type === "success" ? faCircleCheck : faCircleExclamation
              }
            />
            {message.text}
          </div>
        )}

        {/* Filters */}
        <div className="mb-6 rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by store name or code..."
                className="w-full rounded-xl border border-white/10 bg-[#0b1120] py-3 pl-11 pr-4 outline-none focus:border-pink-500"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-xl border border-white/10 bg-[#0b1120] px-2 py-1 outline-none focus:border-pink-500"
            >
              <option>All</option>
              <option>Active</option>
              <option>Inactive</option>
            </select>
          </div>
        </div>

        {/* Add/Edit form */}
        {showForm && (
          <div className="mb-6 rounded-3xl border border-pink-500/30 bg-white/5 p-6">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-cyan-200 text-lg font-bold">
                {editingId ? "Edit Store" : "Add New Store"}
              </h2>
              <button
                onClick={closeForm}
                className="text-slate-200 bg-blue-600 hover:text-white w-fit px-2 py-1"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm text-gray-200">
                    Store Code *
                  </label>
                  <input
                    type="text"
                    name="storeCode"
                    value={form.storeCode}
                    disabled
                    readOnly
                    className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none opacity-50"
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    Auto-set to your seller code, cannot be changed.
                  </p>
                </div>
                <div>
                  <label className="mb-1 block text-sm text-gray-200">
                    Store Code Suffix
                  </label>

                  <input
                    type="text"
                    name="storeSuffix"
                    value={form.storeSuffix}
                    onChange={handleFormChange}
                    maxLength={5}
                    placeholder="home"
                    className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                  />

                  <p className="mt-1 text-xs text-gray-400">
                    Final Store Code:
                    <span className="ml-1 font-semibold text-cyan-300">
                      {form.storeSuffix
                        ? `${form.storeCode}_${form.storeSuffix}`
                        : form.storeCode}
                    </span>
                  </p>
                </div>

                <div>
                  <label className="mb-1 block text-sm text-slate-300">
                    Store Name *
                  </label>
                  <input
                    type="text"
                    name="storeName"
                    value={form.storeName}
                    onChange={handleFormChange}
                    className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="mb-1 block text-sm text-slate-300">
                    Store Headline
                  </label>
                  <input
                    type="text"
                    name="storeHeadline"
                    value={form.storeHeadline}
                    onChange={handleFormChange}
                    placeholder="A short tagline for your store"
                    className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm text-slate-300">
                    Locality
                  </label>
                  <input
                    type="text"
                    name="locality"
                    value={form.locality}
                    onChange={handleFormChange}
                    className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm text-slate-300">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={form.city}
                    onChange={handleFormChange}
                    className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input
                    type="checkbox"
                    name="isBrandOnly"
                    checked={form.isBrandOnly}
                    onChange={handleFormChange}
                  />
                  Brand Only
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input
                    type="checkbox"
                    name="isInnovationOnly"
                    checked={form.isInnovationOnly}
                    onChange={handleFormChange}
                  />
                  Innovation Only
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input
                    type="checkbox"
                    name="isSampleOnly"
                    checked={form.isSampleOnly}
                    onChange={handleFormChange}
                  />
                  Sample Only
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={form.isActive}
                    onChange={handleFormChange}
                  />
                  Active
                </label>
              </div>

              <div>
                <label className="mb-2 block text-sm text-slate-300">
                  Store Image
                </label>

                {editingId ? (
                  <p className="text-xs text-gray-400">
                    Use the image icon on the store list to change this store's image.
                  </p>
                ) : (
                  <p className="text-xs text-gray-400">
                    Save this store first — you'll be able to add an image right
                    after.
                  </p>
                )}
              </div>

              {/* Section: Store Voice Introduction (TTS) */}
              <div>
                <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-pink-300">
                  Store Voice Introduction (Text-to-Speech)
                </h3>

                <label className="mb-1 block text-sm text-gray-200">
                  Story Script (max 500 characters)
                </label>
                <textarea
                  name="tts"
                  value={form.tts}
                  onChange={handleFormChange}
                  maxLength={500}
                  style={{ height: "100px" }}
                  placeholder="A warm spoken introduction to your store, told like a story customers will hear when they visit..."
                  className="w-full resize-none rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                />
                <div className="mt-1 flex items-center justify-between">
                  <p className="text-xs text-slate-400">
                    {form.tts.length}/500 characters
                  </p>

                  {form.tts.trim() && (
                    <button
                      type="button"
                      onClick={handleToggleSpeakTTS}
                      title={isSpeakingTTS ? "Stop" : "Play Store Introduction"}
                      className={`flex h-8 w-8 items-center justify-center rounded-full transition-all ${
                        isSpeakingTTS
                          ? "bg-pink-500 text-white"
                          : "bg-white/10 text-pink-300 hover:bg-white/20"
                      }`}
                    >
                      <FontAwesomeIcon icon={isSpeakingTTS ? faVolumeXmark : faVolumeHigh} />
                    </button>
                  )}
                </div>

                <div className="mt-3 flex flex-col w-full gap-2">
                  <button
                    type="button"
                    onClick={toggleGenAIHelp}
                    className={`w-fit px-2 py-1 rounded-sm text-[10px] font-bold transition-all ${
                      enableGenAIHelp
                        ? "bg-purple-500 text-white"
                        : "bg-[#111827] text-purple-300 border border-purple-500/30"
                    }`}
                  >
                    {enableGenAIHelp ? "🙈 Hide GenAI" : "🤖 Ask GenAI"}
                  </button>

                  {enableGenAIHelp && (
                    <div className="w-full bg-[#111827] rounded-2xl border border-purple-500/20 p-2">
                      <AILightComponent
                        mode="herkart_store_tts_script"
                        param1={"🗣️ AI Store Story Generator"}
                        param2={`
You are a warm, gifted storyteller who writes short spoken introductions for women entrepreneurs' private stores on HerKart, a marketplace celebrating women-led businesses.

Store Details:
Store Name: ${form.storeName || "N/A"}
Store Headline/Tagline: ${form.storeHeadline || "N/A"}
Store Code: ${form.storeCode || "N/A"}

Your task:
Write ONE short, warm, story-driven spoken introduction that a browser Text-to-Speech engine will read aloud to a customer arriving at this store for the first time — like the seller herself is welcoming them in.

STRICT RULES:
1. Output ONLY the final spoken script text. No headings, labels, emojis, markdown, quotation marks, bullet points.
2. Maximum 500 characters INCLUDING spaces and punctuation. Count carefully and stay under the limit.
3. Write it as a warm, personal, story-telling welcome — not a sales pitch or product list. Think "the heart behind this store," not "our catalogue."
4. Use short, natural spoken sentences — avoid long compound sentences or abbreviations.
5. Do NOT invent facts, product names, or personal history not present in the details above. If little is given, keep it warm and general rather than fabricating specifics.
6. End with a short, inviting line, such as "Come see what I've made for you" or "Welcome to my little corner of HerKart".
7. Do not use emojis, asterisks, hashtags, or characters a TTS engine may mispronounce.

Return only the final script text, ready to paste directly into the store's TTS field.
`}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-xl bg-pink-600 w-fit px-2 py-1 font-semibold hover:bg-pink-500 disabled:opacity-60"
                >
                  {saving
                    ? "Saving..."
                    : editingId
                      ? "Update Store"
                      : "Create Store"}
                </button>
                <button
                  type="button"
                  onClick={closeForm}
                  className="rounded-xl border border-white/10 w-fit px-2 py-1 hover:bg-white/10"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="py-16 text-center text-slate-400">
            Loading your stores...
          </div>
        ) : filteredStores.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-white/10 bg-white/5 p-10 text-center">
            <FontAwesomeIcon
              icon={faStore}
              className="mx-auto mb-4 text-4xl text-slate-500"
            />
            <h3 className="text-xl font-semibold text-gray-200">
              No stores yet
            </h3>
            <p className="mt-2 text-sm text-slate-200">
              {stores.length === 0
                ? "Create your first private store to get started."
                : "No stores match your search or filter."}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredStores .filter((store) => store._id !== editingId) .map((store) => (
              <div
                key={store._id}
                className="flex flex-col gap-4 rounded-2xl border border-white/10 bg-white/5 p-4 md:flex-row md:items-center"
              >
                <img
                  src={
                    store.storeImage ||
                    "https://placehold.co/100x100/18181b/f9a8d4?text=Store"
                  }
                  alt={store.storeName}
                  className="h-16 w-16 rounded-xl object-cover"
                />

                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold text-gray-200">
                      {store.storeName}
                    </h3>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                        store.isActive
                          ? "bg-emerald-500/20 text-emerald-300"
                          : "bg-red-500/20 text-red-300"
                      }`}
                    >
                      {store.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <p className="text-sm text-slate-200">
                    Code: {store.storeCode}
                  </p>
                  {store.storeHeadline && (
                    <p className="text-sm text-slate-200">
                      {store.storeHeadline}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEditForm(store)}
                    title="Edit"
                    className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
                  >
                    <FontAwesomeIcon icon={faPen} />
                  </button>

                  <button
                    onClick={() =>
                      setUploaderForId(
                        uploaderForId === store._id ? null : store._id,
                      )
                    }
                    title="Change Image"
                    className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
                  >
                    <FontAwesomeIcon icon={faImage} />
                  </button>

                  <button
                    onClick={() => setLinkForId(linkForId === store._id ? null : store._id)}
                    title="Copy Store Link"
                    className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
                  >
                    <FontAwesomeIcon icon={faLink} />
                  </button>

                  {store.isActive ? (
                    <button
                      onClick={() => setConfirmDeactivateId(store._id)}
                      title="Deactivate"
                      className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-600/20 text-red-300 hover:bg-red-600/30"
                    >
                      <FontAwesomeIcon icon={faBan} />
                    </button>
                  ) : (
                    <button
                      onClick={() => handleReactivate(store._id)}
                      title="Reactivate"
                      className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600/20 text-emerald-300 hover:bg-emerald-600/30"
                    >
                      <FontAwesomeIcon icon={faCircleCheck} />
                    </button>
                  )}
                </div>

                {uploaderForId === store._id && (
                  <div className="w-full md:w-auto">
                    <WebpImgUploader
                      label="Add/Edit Store Image"
                      value={store.storeImage}
                      token={authState?.token}
                      uploadUrl="/upload-herkartstoreimage"
                      extraHeaders={{ "x-store-id": store._id }}
                      onChange={(filename, url) =>
                        handleImageChange(store._id, filename, url)
                      }
                      onClose={() => setUploaderForId(null)}
                    />
                  </div>
                )}

                {linkForId === store._id && (
                  <div className="w-full mt-2 rounded-xl border border-white/10 bg-[#0b1120] p-3 flex flex-col md:flex-row md:items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={buildStoreLink(store)}
                      onFocus={(e) => e.target.select()}
                      className="flex-1 rounded-lg border border-white/10 bg-transparent px-3 py-2 text-sm text-slate-200 outline-none"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleCopyLink(buildStoreLink(store))}
                        title="Copy Link"
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
                      >
                        <FontAwesomeIcon icon={faCopy} />
                      </button>
                      <button
                        onClick={() => handleShareOnWhatsApp(store)}
                        title="Share on WhatsApp"
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600/20 text-emerald-300 hover:bg-emerald-600/30"
                      >
                        <FontAwesomeIcon icon={faWhatsapp} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Deactivate confirmation */}
      {confirmDeactivateId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#0b1120] p-6">
            <h3 className="text-lg font-bold text-red-400">
              Deactivate this store?
            </h3>
            <p className="mt-2 text-sm text-slate-200">
              This store will no longer be visible to customers. You can't undo
              this from here.
            </p>
            <div className="mt-4 flex gap-3">
              <button
                onClick={() => confirmDeactivate(confirmDeactivateId)}
                className="flex-1 rounded-xl bg-red-600 w-fit px-2 py-1 font-semibold hover:bg-red-500"
              >
                Yes, Deactivate
              </button>
              <button
                onClick={() => setConfirmDeactivateId(null)}
                className="flex-1 rounded-xl border border-white/10 w-fit px-2 py-1 hover:bg-white/10"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HerKartManageStores;